import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { PageTransition } from "@/components/PageTransition";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const troubleshootingCategories = [
  {
    id: "login",
    title: "Login & account issues",
    items: [
      {
        question: "I didn't receive my invitation email",
        answer: `Check the following:
• Look in your spam/junk folder
• Verify the email address with your producer
• Ask the producer to resend the invitation
• Check if your email provider blocks automated emails

If none of these work, the producer can add you with a different email address.`,
      },
      {
        question: "I forgot my password",
        answer: `Use the "Forgot Password" link on the login page:
1. Click "Forgot Password"
2. Enter your email address
3. Check your email for the reset link
4. Create a new password

The reset link expires after 24 hours.`,
      },
      {
        question: "I can't verify my email",
        answer: `If the verification link doesn't work:
• Check if the link has expired (valid for 24 hours)
• Request a new verification email from the login page
• Make sure you're clicking the link in the same browser
• Try copying and pasting the link instead of clicking

Contact support if the issue persists.`,
      },
      {
        question: "I'm logged in but don't see any productions",
        answer: `This can happen if:
• The producer hasn't published the production yet
• Your invitation was for a different email address
• The production has been archived

Check with your producer to confirm your status.`,
      },
    ],
  },
  {
    id: "notifications",
    title: "Notification issues",
    items: [
      {
        question: "I'm not receiving push notifications",
        answer: `For push notifications to work:

**On iPhone:**
1. Make sure you installed PROduce Crew from the App Store
2. Open Settings → Notifications → PROduce → Allow Notifications
3. In the app, go to Settings → Notifications → Enable all types you want

**On Android:**
1. Make sure you installed PROduce Crew from Google Play
2. Allow notifications when prompted
3. Check Settings → Apps → PROduce → Notifications

**General:**
• Make sure you're logged in
• Check that notifications are enabled in the app settings
• Try disabling and re-enabling notifications`,
      },
      {
        question: "Notifications arrive late or not at all",
        answer: `Delayed notifications can be caused by:
• Phone battery saver mode (disables background activity)
• Do Not Disturb mode
• Poor internet connection at the time of the notification
• The app being closed/force-quit

**Solutions:**
• Exclude PROduce from battery optimization
• Make sure the app has background activity permission
• Keep the app running in the background during events`,
      },
      {
        question: "I get too many notifications",
        answer: `You can customize notifications in Settings:
1. Go to Settings → Notifications
2. Toggle off the notification types you don't want
3. Adjust the reminder time to receive fewer advance warnings

Note: Emergency notifications cannot be disabled.`,
      },
    ],
  },
  {
    id: "gps",
    title: "GPS & location issues",
    items: [
      {
        question: "My location isn't showing on the map",
        answer: `Check these settings:

**On iPhone:**
1. Settings → Privacy → Location Services → On
2. Find PROduce → Select "Always" or "While Using"
3. In the app, enable location sharing in Settings

**On Android:**
1. Settings → Location → On
2. App permissions → PROduce → Allow all the time
3. In the app, enable location sharing in Settings

Also check that GPS is enabled (not just WiFi location).`,
      },
      {
        question: "My location is inaccurate",
        answer: `GPS accuracy can be affected by:
• Indoor locations (GPS works best outdoors)
• Dense buildings or structures
• Weather conditions
• Phone case or cover blocking the antenna

**Tips for better accuracy:**
• Step outside briefly to get a GPS fix
• Enable "High Accuracy" mode in phone settings
• Make sure WiFi is on (helps with indoor positioning)
• Wait 30 seconds after opening the app`,
      },
      {
        question: "Location tracking drains my battery",
        answer: `To reduce battery usage:
1. In the app Settings, reduce tracking frequency
2. Enable "Track only during assignments" if available
3. Carry a power bank for long events

The producer may have configured tracking settings that you cannot change.`,
      },
      {
        question: "The producer can't see my location",
        answer: `Make sure:
• Location sharing is enabled in your app settings
• The phone has location permission for PROduce
• You're at the event location (some producers enable geofencing)
• Your location was recently updated (check "last updated" time)

If all settings are correct, try toggling location sharing off and on.`,
      },
    ],
  },
  {
    id: "schedule",
    title: "Schedule issues",
    items: [
      {
        question: "My schedule isn't showing any assignments",
        answer: `This can happen if:
• You're viewing the wrong day (check the day tabs)
• The producer hasn't created assignments for you yet
• The production hasn't been published

If your colleagues can see their schedules, contact your producer.`,
      },
      {
        question: "My schedule changed without notification",
        answer: `Schedule change notifications require:
• Push notifications to be enabled
• The notification type to be enabled in settings
• The producer to have published the changes

Check the Timeline/Updates section for recent changes.`,
      },
      {
        question: "Times are showing in the wrong timezone",
        answer: `PROduce shows times in the event's local timezone. If times seem wrong:
• Check if your phone's timezone is set correctly
• The producer may have configured the wrong timezone
• Report the issue to your producer to verify settings`,
      },
    ],
  },
  {
    id: "offline",
    title: "Offline & sync issues",
    items: [
      {
        question: "The app isn't working offline",
        answer: `For offline mode to work:
• You must have opened the app while online first
• The app caches data automatically
• Some features require internet (live tracking, real-time updates)

**To prepare for offline:**
1. Open the app while on WiFi
2. Navigate through all sections you'll need
3. The data will be cached for offline access`,
      },
      {
        question: "My data isn't syncing",
        answer: `If data seems outdated:
• Pull down to refresh on any screen
• Check your internet connection
• Close and reopen the app
• Check the "last synced" time in Settings

For persistent issues, try logging out and back in.`,
      },
      {
        question: "The map isn't loading",
        answer: `Map issues can be caused by:
• No internet connection (maps require download)
• The area hasn't been cached for offline use
• Browser restrictions on location/maps

**Solutions:**
• Connect to WiFi and let the map fully load
• Zoom in/out to trigger tile downloads
• Reinstall the app if the map stays blank`,
      },
    ],
  },
  {
    id: "pdf",
    title: "PDF export issues (Producer)",
    items: [
      {
        question: "PDF export is taking too long",
        answer: `Large productions with many crew members or assignments may take longer to generate. Tips:
• Wait at least 30 seconds before trying again
• Try exporting a single day instead of all days
• Export during off-peak hours if possible`,
      },
      {
        question: "PDF is missing content",
        answer: `Check these settings in the export dialog:
• Make sure all desired sections are selected
• Verify date range includes the days you want
• Check if specific crew members are filtered out

For schedule PDFs, make sure assignments exist for the selected timeframe.`,
      },
      {
        question: "PDF styling looks wrong",
        answer: `PDF generation uses a print-optimized format. If styling seems off:
• Try a different browser (Chrome works best)
• Check if your browser has print settings affecting output
• The PDF may look different from the screen view by design`,
      },
    ],
  },
  {
    id: "general",
    title: "General issues",
    items: [
      {
        question: "The app is running slowly",
        answer: `Try these steps:
1. Close other apps running in the background
2. Restart the app
4. Restart your phone
5. Check for available storage space

If problems persist on a specific production, it may have too much data to display efficiently.`,
      },
      {
        question: "I see an error message",
        answer: `When you encounter an error:
1. Note down the exact error message
2. Try refreshing the page or restarting the app
3. Check your internet connection
4. Try again in a few minutes

If the error persists, report it to your producer with the error message and what you were trying to do.`,
      },
      {
        question: "Something looks different than described in this guide",
        answer: `PROduce is continuously updated. If features differ from this documentation:
• The app may have been updated with new features
• Your producer may have certain features enabled/disabled
• Some features may be role-specific

This Field Guide is periodically updated to reflect the latest version.`,
      },
    ],
  },
];

const Troubleshooting = () => {
  useScrollToHash();
  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <main className="container mx-auto max-w-4xl px-6 py-12">
          <div className="mb-4">
            <Link
              to="/field-guide"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Field Guide
            </Link>
          </div>

          <div className="mb-12">
            <h1 className="mb-4 text-4xl font-bold">Troubleshooting</h1>
            <p className="text-xl text-muted-foreground">Common issues and how to resolve them.</p>
          </div>

          <div className="space-y-8">
            {troubleshootingCategories.map((category) => (
              <Card key={category.id} className="border-border bg-card">
                <CardHeader>
                  <CardTitle>{category.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {category.items.map((item, index) => (
                      <AccordionItem key={index} value={`${category.id}-${index}`}>
                        <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-line text-muted-foreground">
                            {item.answer}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Still Need Help */}
          <Card className="mt-12 border-primary/20 bg-primary/5">
            <CardContent className="p-6 text-center">
              <h3 className="mb-2 text-lg font-semibold">Still Need Help?</h3>
              <p className="mb-4 text-muted-foreground">
                If you can't find the answer you're looking for, contact your production manager or
                reach out to PROduce support.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Contact Support
              </Link>
            </CardContent>
          </Card>
        </main>

        {/* Footer */}
        <footer className="mt-12 border-t border-border py-8">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>PROduce Field Guide — Troubleshooting</p>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default Troubleshooting;
